/**
 * EditableField — edição inline de um campo por clique (RF-M8-001, US-08 §2).
 * Validação local M4-TS (RN-M11-002) antes do envio; marcador visual do estado
 * corrente e da prévia local. Marcadores NÃO bloqueiam o envio (RN-GLOBAL-007).
 * Em leitura, mostra o valor (em caixas de dígito para IMEI/ICCID/CPF/CNPJ/INEP, com as
 * posições corrigidas destacadas; "lido → final" nos demais) e as explicações da
 * validação sob o campo (ADR-030). O campo "data" aparece em DD/MM/AAAA na leitura; a edição continua com o valor
 * guardado (AAAA-MM-DD — spec escolas-ordenacao-e-formatos).
 *
 * Perfil `consulta` recebe `somenteLeitura` e não vê o botão de editar.
 * Nenhum dado pessoal é logado (RN-M11-001) — o valor só viaja no PATCH.
 */

import { useState } from 'react';
import { MarcadorPill } from './ConsultaShared';
import { AchadosCampo, CLASSE_NIVEL } from './AchadosCampo';
import { CAMPOS_DIGITOS, DigitosCorrigidos } from './DigitosCorrigidos';
import { formatarDataVisita } from './format';
import { validarLocal, ResultadoLocal } from '../services/validacaoLocal';
import { AchadoCampo, Marcador } from '../services/consulta-types';

export interface EditableFieldProps {
  readonly nomeCampo: string;
  /** O nome mostrado (ADR-048: o grupo "Equipamento" mostra o rótulo); ausente = o nome do campo, como antes. */
  readonly rotulo?: string;
  readonly valorLiteral: string | null;
  readonly valorFinal: string | null;
  readonly marcador: Marcador;
  readonly editadoPorUsuario: boolean;
  /** Explicações da validação (ADR-030); `[]`/ausente em visita anterior a elas. */
  readonly achados?: readonly AchadoCampo[];
  readonly somenteLeitura: boolean;
  /** Chamado ao confirmar. Retorna true se salvou (fecha o campo); false mantém aberto. */
  readonly onSalvar: (campo: string, valor: string | null, comentario?: string) => Promise<boolean>;
}

/** O campo da data da visita: na leitura, DD/MM/AAAA. */
const CAMPO_DATA = 'data';

/** O valor final como a leitura mostra (a data em DD/MM/AAAA; os demais como estão). */
function valorExibido(nomeCampo: string, valorFinal: string | null): string | null {
  return nomeCampo === CAMPO_DATA && valorFinal !== null ? formatarDataVisita(valorFinal) : valorFinal;
}

export function EditableField(props: EditableFieldProps): JSX.Element {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(props.valorFinal ?? '');
  const [comentario, setComentario] = useState('');
  const [salvando, setSalvando] = useState(false);

  const nome = props.rotulo ?? props.nomeCampo;
  const previa: ResultadoLocal = validarLocal(props.nomeCampo, valor);
  // A nota da prévia na cor do nível dela (ADR-035: a observação é cinza).
  const nivelNota = previa.achados.find((a) => a.nivel !== 'OK')?.nivel;

  const iniciar = (): void => {
    setValor(props.valorFinal ?? '');
    setComentario('');
    setEditando(true);
  };
  const cancelar = (): void => setEditando(false);

  const salvar = async (): Promise<void> => {
    setSalvando(true);
    try {
      const ok = await props.onSalvar(props.nomeCampo, valor === '' ? null : valor, comentario || undefined);
      if (ok) {
        setEditando(false);
      }
    } finally {
      setSalvando(false);
    }
  };

  if (!editando) {
    const exibido = valorExibido(props.nomeCampo, props.valorFinal);
    // O lido igual ao que a tela mostra (ex.: "15/03/2014" lido e guardado como 2014-03-15) não é correção à vista.
    const mudou = props.valorLiteral !== null && props.valorLiteral !== props.valorFinal && props.valorLiteral !== exibido;
    const emCaixas = props.valorFinal !== null && CAMPOS_DIGITOS.has(props.nomeCampo) && /\d/.test(props.valorFinal);
    return (
      <div className="campo-row" data-testid={`campo-${props.nomeCampo}`}>
        <span className="campo-nome">{nome}</span>
        <div className="campo-corpo">
          <span className="campo-valor">
            {emCaixas && props.valorFinal !== null ? (
              <>
                <DigitosCorrigidos
                  valorLiteral={props.valorLiteral}
                  valorFinal={props.valorFinal}
                  marcador={props.marcador}
                />
                {mudou && <span className="campo-literal">lido: {props.valorLiteral}</span>}
              </>
            ) : mudou ? (
              <>
                <span className="campo-literal">{props.valorLiteral}</span>
                <span className="campo-seta" aria-hidden="true">
                  →
                </span>
                <span className="sr-only">corrigido para</span>
                <b>{exibido ?? '—'}</b>
              </>
            ) : (
              <span>{exibido ?? '—'}</span>
            )}
            {props.editadoPorUsuario && <span className="pill pill-muted">editado</span>}
          </span>
          <AchadosCampo achados={props.achados ?? []} />
        </div>
        <MarcadorPill marcador={props.marcador} />
        {!props.somenteLeitura && (
          <button
            type="button"
            className="btn btn-ghost"
            aria-label={`Editar ${nome}`}
            onClick={iniciar}
          >
            Editar
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="campo-row campo-edit" data-testid={`campo-edit-${props.nomeCampo}`}>
      <span className="campo-nome">{nome}</span>
      <div className="campo-edit-fields">
        <input
          className="input"
          aria-label={`Valor de ${nome}`}
          value={valor}
          onChange={(e) => setValor(e.target.value)}
        />
        <input
          className="input"
          aria-label="Motivo da correção"
          placeholder="Motivo da correção (opcional)"
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
        />
        <div className="campo-edit-feedback">
          <MarcadorPill marcador={previa.marcador} />
          {previa.nota && <span className={nivelNota ? CLASSE_NIVEL[nivelNota] : 'muted'}>{previa.nota}</span>}
        </div>
        <div className="campo-edit-acoes">
          <button type="button" className="btn btn-primary" onClick={salvar} disabled={salvando}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={cancelar} disabled={salvando}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
